import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import {
  Wifi,
  WifiOff,
  Radio,
  Map as MapIcon,
  Calendar,
  RefreshCw,
  Settings,
  AlertCircle,
  Terminal,
  Activity,
  Clock,
  ClockAlert,
  CircleSlash,
} from 'lucide-react';
import { useOrgTimezone, useOrgTimezoneOrNull } from '@/utils/timezone';
import { siteOffsetSec } from '@/utils/irrigationClock';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/PageHeader';
import EspNowScheduleBuilder from '../../components/irrigation/EspNowScheduleBuilder';
import RainSkipPanel from '../../components/irrigation/RainSkipPanel';
import EspNowLiveMap from '../../components/irrigation/EspNowLiveMap';
import orchardService from '../../services/orchard';
import espnowService, {
  type EspNowController,
  type EspNowRouter,
  type MasterPump,
  type ScheduleProgram,
  type EspNowEvent,
  type FieldCommand,
  type NodeScheduleState,
  type LiveMapData,
  getRelayState,
  usableRelays,
} from '../../services/espnowIrrigation';
import { useAppRefresh } from '../../hooks/useAppRefresh';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { isOrgAdmin, isOrgSuperAdmin } from '../../utils/auth';
import { errorMessage } from '../../utils/apiResponse';
import '../../styles/GlobalPageStyles.css';
import { nodeNoun } from '../../utils/nodeLabel';
import { localDaySec } from '../../utils/irrigationRuns';
import { buildLiveMapStatus, type ValveStatus } from '../../utils/liveMapStatus';
import { relayCommandBusy, type RelayCommand } from '../../utils/relayCommand';
import MeshDiagnostics from '../../components/irrigation/MeshDiagnostics';
import RecentActivity from '../../components/irrigation/RecentActivity';
import NodeEnrolment from '../../components/irrigation/NodeEnrolment';
import BridgeUpdatePanel from '../../components/irrigation/BridgeUpdatePanel';
import StopWateringButton from '../../components/irrigation/StopWateringButton';
import WateringHoldBanner from '../../components/irrigation/WateringHoldBanner';
import useWateringStop from '../../hooks/useWateringStop';
import { CommandsTable, EventsTable } from '../../components/irrigation/controller/EventTables';
import { RouterDetailCard } from '../../components/irrigation/controller/RouterDetailCard';
import { SensorsTab } from '../../components/irrigation/controller/SensorsTab';
import { SettingsTab } from '../../components/irrigation/controller/SettingsTab';
import { MANUAL_RUN_MAX_LABEL, MANUAL_RUN_MAX_SEC, RELAY_ACK_WAIT_MS, RELAY_POLL_MS, RELAY_READ_WAIT_MS, sleep } from '../../components/irrigation/controller/relayTiming';

type Tab = 'routers' | 'schedules' | 'map' | 'events' | 'sensors' | 'settings';
const TABS: Tab[] = ['routers', 'schedules', 'map', 'events', 'sensors', 'settings'];

export default function EspNowControllerDetail() {
  const { controllerId } = useParams<{ controllerId: string }>();
  const orgTimezone = useOrgTimezone();
  const orgTzOrNull = useOrgTimezoneOrNull();
  const { user } = useAuth();
  // super_admin still gates the OTA firmware-update buttons (the backend
  // enforces super_admin on POST /espnow/controllers/:id/ota).
  const isSuperAdmin = isOrgSuperAdmin(user);
  // Matches the endpoint's own gate (irrigationAdmin = admin or super_admin), so
  // the button is not offered to somebody the server will refuse.
  const isAdmin = isOrgAdmin(user);
  // irrigation.admin seat holders can change farm assignment, set up the
  // controller's sensor device, and manage sensor channels. Read access is
  // available to any user on the irrigation subscription.
  const { hasAccess: canIrrigationAdmin } = useModuleLicense('irrigation', 'admin');
  // Stopping needs no seat beyond the module — whoever can see that something
  // is wrong has to be able to stop it. Resuming needs the admin seat, which is
  // what the endpoint enforces; the banner says so rather than going blank.
  const wateringStop = useWateringStop(controllerId);
  const [controller, setController] = useState<EspNowController | null>(null);
  // 'Node' on XBee sites, 'Router' on ESP-NOW - see utils/nodeLabel.
  const noun = nodeNoun(controller?.transport);
  const [routers, setRouters] = useState<EspNowRouter[]>([]);
  const [scrolledToNode, setScrolledToNode] = useState<string | null>(null);
  const [masterPumps, setMasterPumps] = useState<MasterPump[]>([]);
  const [programs, setPrograms] = useState<ScheduleProgram[]>([]);
  const [events, setEvents] = useState<EspNowEvent[]>([]);
  // What we asked the site to do, and what it answered. Sits beside the
  // events because the two halves of a diagnosis were split: the events say
  // what the site did on its own, the commands say what it did with us.
  const [commands, setCommands] = useState<FieldCommand[]>([]);
  // What each node reports it is actually holding (§3.7). Distinct from the
  // programs above, which are only what the server intended to send.
  const [scheduleState, setScheduleState] = useState<NodeScheduleState[]>([]);
  // The tab lives in the URL so the farm page can link straight at one: its
  // map preview opens ?tab=map, a schedule row opens ?tab=schedules&program=X.
  // Routers stays the bare-URL default, so existing links are unaffected.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const tab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : 'routers';
  const focusProgramId = searchParams.get('program');
  // ?node=<mac> rings that node's card and scrolls to it: the farm's Setup tab
  // links each node straight here.
  const focusNodeMac = searchParams.get('node')?.toUpperCase() ?? null;
  const setTab = useCallback((next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'routers') params.delete('tab');
    else params.set('tab', next);
    // The focused program belongs to the link that arrived, not to the tab the
    // reader moves on to.
    if (next !== 'schedules') params.delete('program');
    if (next !== 'routers') params.delete('node');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);
  // Once, when the linked node's card first renders. Later polls must not
  // pull the page back to it while somebody scrolls elsewhere.
  useEffect(() => {
    if (!focusNodeMac || tab !== 'routers' || scrolledToNode === focusNodeMac) return;
    const found = routers.find((r) => r.router_mac.toUpperCase() === focusNodeMac);
    if (!found) return;
    document.getElementById(`node-${found.router_mac}`)?.scrollIntoView({ block: 'center' });
    setScrolledToNode(focusNodeMac);
  }, [focusNodeMac, tab, routers, scrolledToNode]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [statusRefreshing, setStatusRefreshing] = useState(false);
  const [timeSyncing, setTimeSyncing] = useState(false);
  const [otaSending, setOtaSending] = useState<string | null>(null); // 'master' or router_mac
  const [farms, setFarms] = useState<Array<{ id: number; name: string }>>([]);
  const [savingFarm, setSavingFarm] = useState(false);
  // Every manual tap still being followed, keyed `${routerMac}:${relayIndex}`,
  // from the POST through the site's acknowledgement to the node's own reading
  // of the output. See utils/relayCommand — the two are different claims and
  // the tile colours them differently.
  const [relayCommands, setRelayCommands] = useState<Map<string, RelayCommand>>(new Map());
  // One token per relay, bumped on every tap. A tap that is superseded — the
  // operator stopping a 60 s test run early, say — must not have its trailing
  // read-back write a verdict over the newer tap's.
  const relayRun = useRef<Map<string, number>>(new Map());
  // The bridge's own view of today's programs, held with the moment it was read
  // so its `now_sec` can be advanced by wall clock between polls. Only fetched
  // for sites whose timers the bridge holds — see `bridgeTimed` below.
  const [liveMap, setLiveMap] = useState<{ data: LiveMapData; at: number } | null>(null);

  const load = useCallback(async () => {
    if (!controllerId) return;
    try {
      const [ctrls, rts, pumps, progs, evts, cmds, sched, farmsRes] = await Promise.all([
        espnowService.getControllers(),
        espnowService.getRouters(controllerId).catch(() => [] as EspNowRouter[]),
        espnowService.getMasterPumps(controllerId).catch(() => [] as MasterPump[]),
        espnowService.getPrograms(controllerId).catch(() => [] as ScheduleProgram[]),
        espnowService.getEvents(controllerId, 50).catch(() => [] as EspNowEvent[]),
        espnowService.getCommandLog(controllerId, 50).catch(() => [] as FieldCommand[]),
        espnowService.getScheduleState(controllerId).catch(() => [] as NodeScheduleState[]),
        orchardService.getFarms(1, 100).catch(() => null),
      ]);
      const farmList = farmsRes && farmsRes.success ? (farmsRes.data?.farms ?? []) : [];
      setFarms(farmList.map((f: { id: number; name: string }) => ({ id: f.id, name: f.name })));
      const ctrl = ctrls.find(c => c.controller_id === controllerId) ?? null;
      setController(ctrl);
      setNameValue(ctrl?.controller_name ?? controllerId ?? '');
      setRouters(rts);
      setMasterPumps(pumps);
      setPrograms(progs);
      setEvents(evts);
      setCommands(cmds);
      setScheduleState(sched);
    } catch {
      setError('Failed to load controller data');
    } finally {
      setLoading(false);
    }
  }, [controllerId]);

  useAppRefresh(useCallback(() => { load(); }, [load]));

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      if (tab === 'routers' || tab === 'events') {
        espnowService.getRouters(controllerId!).then(setRouters).catch(() => {});
        espnowService.getEvents(controllerId!, 50).then(setEvents).catch(() => {});
        espnowService.getCommandLog(controllerId!, 50).then(setCommands).catch(() => {});
      }
      // Nodes only self-report every 5 min, but they also post immediately on
      // their first time sync — that is the moment `runnable` flips true across
      // a whole node, so poll often enough to redraw without a 5 min lag.
      if (tab === 'routers' || tab === 'schedules') {
        espnowService.getScheduleState(controllerId!).then(setScheduleState).catch(() => {});
      }
    }, 15_000);
    return () => clearInterval(interval);
  }, [load, tab, controllerId]);

  // Where a site's timers physically live decides which feed can answer "is
  // this valve part of a program, and is it due now".
  //
  // Nothing is ever pushed to an XBee site's nodes — the laptop bridge holds
  // the clock — so `schedule-state` comes back empty for every relay on every
  // node, and the relay tiles below showed no countdown, no "armed" and no sign
  // that a valve the schedule opens in three minutes is scheduled at all. The
  // same silence covered a valve whose window was open RIGHT NOW: the tile read
  // a bare "OFF", off a bitmask that on this site is only refreshed when
  // somebody runs Check State.
  //
  // The bridge's timeline is the other half of the pair the irrigation landing
  // page already learnt to read, and `buildLiveMapStatus` is the same
  // derivation the live map times its zones from — so the two cannot disagree.
  const bridgeTimed = controller?.transport === 'xbee';
  useEffect(() => {
    if (!controllerId || !bridgeTimed || (tab !== 'routers' && tab !== 'map')) return;
    let cancelled = false;
    const pull = () => espnowService.getLiveMap(controllerId)
      .then(data => { if (!cancelled) setLiveMap({ data, at: Date.now() }); })
      .catch(() => {});
    pull();
    const id = setInterval(pull, 20_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [controllerId, bridgeTimed, tab]);

  // The SITE's offset, not the viewer's browser timezone — see siteOffsetSec.
  const bridgeOffsetSec = siteOffsetSec(orgTzOrNull, controller?.timezone_offset);

  async function removeRouter(mac: string, label: string) {
    // Typed confirmation is overkill; a named one is not. The address is what the
    // operator sees on the card, and removing the wrong node means deleting its
    // relay names and zone assignments as well.
    if (!confirm(
      `Remove ${label} (${mac}) from this controller?

`
      + 'Its relay names and zone assignments go with it. Use this when a node has '
      + 'been replaced or taken out of service - the replacement has a different '
      + 'address and will appear on its own.'
    )) return;
    try {
      await espnowService.deleteRouter(controllerId!, mac);
      load();
    } catch (err) {
      // The 409 case is the useful one: it names the programs still watering this
      // node, which is exactly what has to be repointed first.
      alert(errorMessage(err, 'Could not remove this node'));
    }
  }

  async function saveName() {
    if (!controller) return;
    setSavingName(true);
    try {
      const updated = await espnowService.updateController(controller.controller_id, {
        controller_name: nameValue,
      });
      setController(prev => prev ? { ...prev, controller_name: updated.controller_name } : prev);
      setEditingName(false);
    } catch {
      setError('Failed to update name');
    } finally {
      setSavingName(false);
    }
  }

  async function handleRouterMapPlace(routerMac: string, lat: number, lng: number) {
    if (!controller) return;
    try {
      const updated = await espnowService.updateRouterLocation(routerMac, controller.controller_id, lat, lng);
      setRouters(prev => prev.map(r => r.router_mac === routerMac ? { ...r, lat: updated.lat, lng: updated.lng } : r));
    } catch {
      setError('Failed to save router location');
    }
  }

  async function handleUpdateRelayLabels(
    routerMac: string,
    labels: Record<string, string>,
    relayCount?: number | null,
    disabledRelays?: number[],
  ) {
    if (!controller) return;
    try {
      const updated = await espnowService.updateRouterLabels(
        routerMac, controller.controller_id, labels, relayCount, disabledRelays,
      );
      setRouters(prev => prev.map(r => r.router_mac === routerMac
        ? {
            ...r,
            relay_labels: updated.relay_labels,
            relay_count: updated.relay_count,
            disabled_relays: updated.disabled_relays ?? [],
          }
        : r));
    } catch {
      setError('Failed to save labels');
    }
  }

  async function handleUpdateRouterName(routerMac: string, name: string) {
    if (!controller) return;
    try {
      const updated = await espnowService.updateRouterName(routerMac, controller.controller_id, name);
      setRouters(prev => prev.map(r => r.router_mac === routerMac ? { ...r, name: updated.name } : r));

      // On an XBee site the name is not just a label we keep — it is the
      // radio's Node Identifier. Writing it here too (NI + WR) means the name
      // survives a re-discovery and shows up in any other tool that talks to
      // the mesh. Without this the server and the hardware disagree the moment
      // someone runs Find Nodes, and the discovered NI overwrites the label.
      if (controller.transport === 'xbee') {
        await espnowService.runDiagnostic(controller.controller_id, {
          action: 'SET_NODE_NAME',
          router_mac: routerMac,
          name,
        });
      }
    } catch {
      setError(`Failed to rename ${nodeNoun(controller.transport).oneLower}`);
    }
  }

  async function handleAddMasterPump(routerMac: string, relayIndex: number, label: string) {
    if (!controller) return;
    try {
      const pump = await espnowService.addMasterPump(controller.controller_id, {
        router_mac: routerMac,
        relay_index: relayIndex,
        label: label || null,
      });
      setMasterPumps(prev => [...prev, pump]);
    } catch {
      setError('Failed to add master pump');
    }
  }

  async function handleUpdateMasterPump(pumpId: string, data: { router_mac?: string; relay_index?: number; label?: string | null }) {
    try {
      const updated = await espnowService.updateMasterPump(pumpId, data);
      setMasterPumps(prev => prev.map(p => p.id === pumpId ? updated : p));
    } catch {
      setError('Failed to update master pump');
    }
  }

  async function handleDeleteMasterPump(pumpId: string) {
    try {
      await espnowService.deleteMasterPump(pumpId);
      setMasterPumps(prev => prev.filter(p => p.id !== pumpId));
    } catch {
      setError('Failed to delete master pump');
    }
  }

  async function requestStatusRefresh() {
    if (!controllerId || statusRefreshing) return;
    setStatusRefreshing(true);
    try {
      // Snapshot current relay_state_updated_at per router so we can detect fresh data
      const baseline: Record<string, string | null> = {};
      routers.forEach(r => { baseline[r.router_mac] = r.relay_state_updated_at; });

      await espnowService.sendCommand(controllerId, { type: 'GET_STATUS' });

      // Poll relay-states until any router has a newer timestamp (max 30s)
      const deadline = Date.now() + 30_000;
      while (Date.now() < deadline) {
        await new Promise(res => setTimeout(res, 2000));
        const fresh = await espnowService.getRelayStates(controllerId);
        const updated = fresh.some(r => r.relay_state_updated_at !== baseline[r.router_mac]);
        if (updated) {
          setRouters(prev => prev.map(r => {
            const s = fresh.find(f => f.router_mac === r.router_mac);
            return s ? {
              ...r, relay_states: s.relay_states, relay_state_updated_at: s.relay_state_updated_at,
              relay_state_source: s.relay_state_source,
            } : r;
          }));
          break;
        }
      }
    } catch {
      setError('Failed to request status refresh');
    } finally {
      setStatusRefreshing(false);
    }
  }

  /**
   * Broadcast a TIMESYNC. A node with `clock_ok: false` has a dead scheduler —
   * its stored programs are correct but will never fire — so this is the single
   * repair action for the most common silent failure.
   */
  async function sendTimeSync() {
    if (!controllerId || timeSyncing) return;
    setTimeSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      await espnowService.sendCommand(controllerId, { type: 'TIMESYNC' });
      setSuccess('Time sync queued. Nodes pick it up within ~10 s and re-report their schedules.');
      // Nodes post a fresh schedule-state immediately after their first sync;
      // give the mesh a moment, then pull it so `runnable` redraws.
      setTimeout(() => {
        espnowService.getScheduleState(controllerId).then(setScheduleState).catch(() => {});
      }, 12_000);
    } catch {
      setError('Failed to queue time sync');
    } finally {
      setTimeSyncing(false);
    }
  }

  /**
   * Tap a relay, then find out whether anything actually happened.
   *
   * Three things are asked in turn, because they are three different questions:
   *
   *   1. Did the server take the command?  — the POST
   *   2. Did the SITE carry it out?        — the command row reaching CONFIRMED
   *   3. Does the NODE report the output in that state? — a reading taken after
   *
   * Only (3) is evidence that a valve is open. The tile used to answer (1) and
   * paint itself as though it had answered (3), so a command the bridge later
   * refused outright — "failed after 5 attempts: node returned an error status"
   * is a real answer from a real site — still read as a working valve.
   *
   * (3) has to be ASKED for. An XBee node reports its outputs only when
   * something requests them, so waiting for a spontaneous report is waiting for
   * a message nobody is going to send.
   */
  async function handleToggleRelay(routerMac: string, relayIndex: number, currentlyOn: boolean) {
    if (!controllerId || !controller) return;
    const key = `${routerMac}:${relayIndex}`;
    if (relayCommandBusy(relayCommands.get(key))) return;

    const router = routers.find(r => r.router_mac === routerMac);
    const label = router?.relay_labels?.[String(relayIndex)] || `Relay ${relayIndex}`;
    const noun = nodeNoun(controller.transport).oneLower;
    const want = !currentlyOn;
    const verb = want ? 'turn on' : 'turn off';

    // No confirmation either way: a tap is the whole gesture, and the worst a
    // mistaken one can do is run the relay for MANUAL_RUN_MAX_SEC.

    const run = (relayRun.current.get(key) ?? 0) + 1;
    relayRun.current.set(key, run);
    /**
     * Writes a phase, unless a newer tap on this relay has taken over.
     *
     * `asked` defaults to what this tap asked for, but the trailing read after
     * a manual run times out is asking the opposite question — and a verdict
     * filed under the wrong intent paints a confirmed-OFF tile green.
     */
    const mark = (
      phase: RelayCommand['phase'],
      detail: string | null = null,
      asked: boolean = want,
    ) => {
      if (relayRun.current.get(key) !== run) return false;
      setRelayCommands(prev => new Map(prev).set(key, { want: asked, phase, detail, at: Date.now() }));
      return true;
    };
    const current = () => relayRun.current.get(key) === run;

    mark('sending');
    setError(null);

    let cmdId: string;
    try {
      const res = await espnowService.sendCommand(controllerId, want
        ? { type: 'SET_RELAY', router_mac: routerMac, relay: relayIndex, duration_sec: MANUAL_RUN_MAX_SEC }
        : { type: 'CLEAR_RELAY', router_mac: routerMac, relay: relayIndex });
      cmdId = res.cmd_id;
    } catch {
      if (mark('failed', 'The command could not be queued')) setError(`Failed to ${verb} ${label}`);
      return;
    }

    // ── The site's acknowledgement ────────────────────────────────────────────
    const ackBy = Date.now() + RELAY_ACK_WAIT_MS;
    let acked = false;
    while (Date.now() < ackBy && current()) {
      await sleep(RELAY_POLL_MS);
      const log = await espnowService.getCommandLog(controllerId, 20).catch(() => null);
      const mine = log?.find(c => c.cmd_id === cmdId);
      if (!mine) continue;
      if (mine.status === 'ERROR') {
        // The site's own words. They name the node and the channel, which is
        // more than any sentence this page could compose.
        if (mark('failed', mine.detail)) {
          setError(mine.detail || `The site refused to ${verb} ${label}`);
        }
        return;
      }
      if (mine.status === 'CONFIRMED' || mine.status === 'OK') { acked = true; break; }
    }
    if (!current()) return;
    if (!acked) {
      mark('unconfirmed', `The site has not acknowledged the command to ${verb} ${label}`);
      return;
    }
    mark('acked');

    // ── The node's own reading ────────────────────────────────────────────────
    if (!(await confirmRelay(routerMac, relayIndex, want, label, noun, mark, current))) return;

    // A manual run stops itself after MANUAL_RUN_MAX_SEC. Nothing reports that,
    // so a tile confirmed green would sit there claiming an open valve long
    // after the water stopped. Read it back once more when the run is due to
    // have ended, and let the answer stand.
    if (want) {
      await sleep(MANUAL_RUN_MAX_SEC * 1000);
      if (!current()) return;
      await confirmRelay(routerMac, relayIndex, false, label, noun, mark, current, true);
    }
  }

  /**
   * Wait for the node's own reading of the output.
   *
   * Nothing is asked for here. Both ends of the link sample the pins after a
   * relay changes and post what they find — the XBee bridge through
   * `_read_back_states` a couple of seconds after the boundary, an ESP-NOW node
   * on its own change report — so this only has to watch
   * `relay_state_updated_at` move. Issuing a READ_STATES from the browser as
   * well put a second frame on the mesh to learn what was already on its way.
   *
   * Returns false when nothing came back, or when the answer contradicts what
   * was asked for — either way the caller has nothing left to follow.
   *
   * `trailing` is the read taken once a manual run should have stopped itself.
   * Nothing was commanded there, so a relay still reporting on is a valve left
   * open rather than a command that missed, and it is worded as one.
   */
  async function confirmRelay(
    routerMac: string,
    relayIndex: number,
    want: boolean,
    label: string,
    noun: string,
    mark: (phase: RelayCommand['phase'], detail?: string | null, asked?: boolean) => boolean,
    current: () => boolean,
    trailing = false,
  ): Promise<boolean> {
    if (!controllerId || !controller || !current()) return false;
    mark('confirming', null, want);

    // Taken now rather than from the render closure: the 15 s poll may have
    // moved it while the command was in flight, and a baseline that is already
    // stale would read the poll's own refresh as this command's answer.
    let before = (await espnowService.getRelayStates(controllerId).catch(() => null))
      ?.find(r => r.router_mac === routerMac)?.relay_state_updated_at ?? null;

    const readBy = Date.now() + RELAY_READ_WAIT_MS;
    while (Date.now() < readBy && current()) {
      await sleep(RELAY_POLL_MS);
      const fresh = await espnowService.getRelayStates(controllerId).catch(() => null);
      const row = fresh?.find(f => f.router_mac === routerMac);
      if (!row || row.relay_state_updated_at === before) continue;

      setRouters(prev => prev.map(r => r.router_mac === routerMac
        ? {
          ...r, relay_states: row.relay_states, relay_state_updated_at: row.relay_state_updated_at,
          relay_state_source: row.relay_state_source,
        }
        : r));

      // The bridge's report of the ack it already gave us, not a reading of
      // the pins. Keep waiting for the read that follows it.
      if (row.relay_state_source === 'ack') {
        before = row.relay_state_updated_at;
        continue;
      }

      const actual = getRelayState(row.relay_states, relayIndex);
      if (actual === want) { mark('confirmed', null, want); return true; }
      const said = trailing
        ? `${label} should have stopped itself after ${MANUAL_RUN_MAX_LABEL} and the ${noun} still reports it on`
        : `${label} was commanded ${want ? 'on' : 'off'} and the ${noun} reports it ${actual ? 'on' : 'off'}`;
      if (mark('mismatch', said, want)) setError(said);
      return false;
    }

    if (current()) {
      mark('unconfirmed', `No output reading came back from the ${noun} for ${label}`, want);
    }
    return false;
  }

  async function handleOtaUpdate(target: 'master' | 'router', routerMac?: string) {
    if (!controllerId) return;
    const key = target === 'master' ? 'master' : routerMac!;
    const label = target === 'master'
      ? 'master firmware'
      : `router ${routerMac?.slice(-6) ?? ''}`;
    if (!window.confirm(
      `Queue OTA firmware update for ${label}?\n\n` +
      `The device will download the latest firmware on its next command poll ` +
      `and reboot. This is typically a last-resort action.`
    )) return;

    setOtaSending(key);
    setError(null);
    setSuccess(null);
    try {
      await espnowService.queueOtaUpdate(controllerId, target, routerMac);
      setSuccess(
        target === 'master'
          ? 'OTA update queued. Master will download & reboot on its next poll (within 10s).'
          : `OTA update queued for ${routerMac}. Master will push creds + URL over ESP-NOW.`
      );
    } catch {
      setError(`Failed to queue OTA update for ${label}`);
    } finally {
      setOtaSending(null);
    }
  }

  async function handleFarmChange(farmId: number | null) {
    if (!controller) return;
    if (!canIrrigationAdmin) {
      setError('An Irrigation Admin license seat is required to change farm assignment.');
      return;
    }
    setSavingFarm(true);
    try {
      const updated = await espnowService.updateControllerFarm(controller.controller_id, farmId);
      setController(prev => prev ? { ...prev, farm_id: updated.farm_id } : prev);
    } catch {
      setError('Failed to update farm assignment');
    } finally {
      setSavingFarm(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6 flex justify-center items-center h-64 text-gray-400">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (!controller) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="flex items-center gap-2 p-3 m-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}
        <div className="p-6 text-center text-gray-400">
          Controller not found or not assigned to your organisation.
        </div>
      </div>
    );
  }

  // A site on a farm is scheduled from the farm page's Schedules tab. An old
  // link (?tab=schedules&program=X) is carried there with its program.
  const farmSchedulesHref = controller.farm_id
    ? `/irrigation/farms/${controller.farm_id}?tab=schedules&site=${encodeURIComponent(controller.controller_id)}${
      focusProgramId ? `&program=${encodeURIComponent(focusProgramId)}` : ''}`
    : null;
  if (tab === 'schedules' && farmSchedulesHref) {
    return <Navigate to={farmSchedulesHref} replace />;
  }

  // Device-reported schedule snapshots, keyed by the MAC they belong to. Plain
  // computation rather than useMemo so it stays below the early-return guards.
  const schedByMac: Record<string, NodeScheduleState> = {};
  scheduleState.forEach(s => { schedByMac[s.router_mac] = s; });

  // The same snapshot for a bridge-timed site, keyed `mac:relay`. Folded into
  // the LOCAL day: the timeline's seconds are UTC but the programs in it were
  // selected by today's LOCAL weekday, and comparing the two raw reads a +10
  // site's whole morning as already past. See localDaySec.
  const bridgeValves: Map<string, ValveStatus> | null = liveMap
    ? buildLiveMapStatus({
        liveData: liveMap.data,
        routers,
        transport: controller?.transport ?? null,
        nowSec: localDaySec(
          liveMap.data.now_sec + Math.floor((Date.now() - liveMap.at) / 1000),
          bridgeOffsetSec
        ),
        offsetSec: bridgeOffsetSec,
        nowMs: Date.now(),
      }).byKey
    : null;

  // How many outputs are reported on right now, for the stop confirmation to
  // put a number on what is about to close. Counted off the bitmask rather than
  // off the schedule: what a program says should be running is not what is
  // running, and this is the figure somebody is about to act on. usableRelays
  // keeps a relay nobody wired out of the count.
  const openRelayCount = routers.reduce((total, r) => {
    const mask = Number(r.relay_states ?? 0);
    return total + usableRelays(r, controller?.transport)
      .filter(idx => (mask & (1 << idx)) !== 0).length;
  }, 0);

  // Fleet health, straight off what the hardware says — not off the programs
  // the server believes it pushed.
  const nodesWithoutClock = scheduleState.filter(s => !s.clock_ok);
  const deadSchedules = scheduleState.flatMap(s =>
    s.relays
      .filter(r => r.enabled && !r.runnable)
      .map(r => ({ node: s, relay: r }))
  );

  function relayDisplayName(mac: string, relayIndex: number): string {
    const r = routers.find(x => x.router_mac === mac);
    return r?.relay_labels?.[String(relayIndex)] || `Relay ${relayIndex}`;
  }

  function nodeDisplayName(mac: string): string {
    const r = routers.find(x => x.router_mac === mac);
    return r?.name || mac.slice(-6);
  }

  return (
    <>
      <PageHeader
        title={controller.controller_name || controller.controller_id}
        subtitle={[
          controller.controller_id,
          controller.online ? 'Online' : 'Offline',
          // An online laptop with no radio is the case this call-out exists
          // for: without it the header says "Online" while every node reads
          // offline, and the two together describe a farm-wide node failure
          // that is not happening.
          controller.online && controller.coordinator_connected === false
            ? 'no coordinator' : null,
          controller.device_type || null,
          controller.firmware_version != null ? `fw v${controller.firmware_version}` : null,
        ].filter(Boolean).join(' · ')}
        icon={controller.online ? <Wifi className="w-5 h-5 text-green-500" /> : <WifiOff className="w-5 h-5 text-gray-400" />}
        actionButtons={
          // Offered even when the controller reads offline. "Offline" means the
          // server has not heard from it lately, which is not the same as the
          // site having stopped watering — an XBee bridge whose uplink dropped
          // is still running its own timers perfectly. The command queues and is
          // collected when it comes back, and the hold applies immediately
          // either way.
          <StopWateringButton
            controllerName={controller.controller_name || controller.controller_id}
            openValves={openRelayCount}
            busy={wateringStop.busy}
            disabled={Boolean(wateringStop.status?.paused)}
            onStop={(reason) => wateringStop.stop(reason)}
          />
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div>
          {/* First thing on the page, above even the error strip: a site that
              will not water until somebody says so explains every other empty
              answer below it. */}
          <WateringHoldBanner
            status={wateringStop.status}
            timezone={orgTimezone}
            canResume={canIrrigationAdmin}
            busy={wateringStop.busy}
            onResume={wateringStop.resume}
            className="mb-4"
          />
          {wateringStop.error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 mb-4">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {wateringStop.error}
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 mb-4">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">×</button>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 mb-4">
              {success}
              <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-600">×</button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto">
            {([
              { id: 'routers', label: noun.many, icon: <Radio className="w-4 h-4" /> },
              { id: 'schedules', label: 'Schedules', icon: <Calendar className="w-4 h-4" /> },
              { id: 'map', label: 'Live Map', icon: <MapIcon className="w-4 h-4" /> },
              { id: 'sensors', label: 'Sensors', icon: <Activity className="w-4 h-4" /> },
              { id: 'events', label: 'Events', icon: <Terminal className="w-4 h-4" /> },
              { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
            ] as { id: Tab; label: string; icon: React.ReactNode }[]).map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* Routers tab — live status only */}
          {tab === 'routers' && (
            <div className="space-y-4">
              {/* Fleet-level schedule health, from device-reported state (§3.7). */}
              {nodesWithoutClock.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
                  <ClockAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">
                      {nodesWithoutClock.length === 1 ? '1 node has' : `${nodesWithoutClock.length} nodes have`} no clock.
                    </span>{' '}
                    Nothing on {nodesWithoutClock.length === 1 ? 'it' : 'them'} will water until a time sync
                    arrives, however good the schedules look —{' '}
                    {nodesWithoutClock.map(s => nodeDisplayName(s.router_mac)).join(', ')}.
                    <button
                      onClick={() => sendTimeSync()}
                      disabled={!controller.online || timeSyncing}
                      className="ml-2 underline font-medium disabled:opacity-50 disabled:no-underline"
                    >
                      {timeSyncing ? 'Sending…' : 'Send time sync'}
                    </button>
                  </div>
                </div>
              )}
              {deadSchedules.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                  <CircleSlash className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">
                      {deadSchedules.length} stored {deadSchedules.length === 1 ? 'schedule' : 'schedules'} will not run.
                    </span>
                    <ul className="mt-1 space-y-0.5 text-xs">
                      {deadSchedules.slice(0, 6).map(({ node, relay }) => (
                        <li key={`${node.router_mac}-${relay.relay}`}>
                          {nodeDisplayName(node.router_mac)} · {relayDisplayName(node.router_mac, relay.relay)}
                          {relay.reason ? ` — ${relay.reason}` : ''}
                        </li>
                      ))}
                      {deadSchedules.length > 6 && (
                        <li className="italic">…and {deadSchedules.length - 6} more.</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Relay status refresh */}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={sendTimeSync}
                  disabled={timeSyncing || !controller.online}
                  title={!controller.online ? 'Controller is offline' : 'Broadcast a time sync to every node'}
                >
                  <Clock className={`w-4 h-4 sm:mr-1.5 ${timeSyncing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">
                    {timeSyncing ? 'Syncing…' : 'Time Sync'}
                  </span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={requestStatusRefresh}
                  disabled={statusRefreshing || !controller.online}
                  title={!controller.online ? 'Controller is offline' : 'Request relay states from all routers'}
                >
                  <RefreshCw className={`w-4 h-4 sm:mr-1.5 ${statusRefreshing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">
                    {statusRefreshing ? 'Refreshing…' : 'Refresh Status'}
                  </span>
                </Button>
              </div>

              {/* Mesh diagnostics — XBee only. ESP-NOW masters do not implement
                  these commands and the server rejects them for that transport,
                  so showing the panel would just offer buttons that always fail. */}
              {controller?.transport === 'xbee' && (
                <div className="mb-4">
                  <MeshDiagnostics
                    controllerId={controller.controller_id}
                    transport={controller.transport}
                    routers={routers}
                    online={controller.online}
                    coordinatorConnected={controller.coordinator_connected}
                    coordinatorLastOk={controller.coordinator_last_ok}
                    coordinatorError={controller.coordinator_error}
                    onRefresh={load}
                    commands={commands}
                    onShowLog={() => setTab('events')}
                  />
                </div>
              )}

              {/* The same list for a site with no mesh diagnostics to sit
                  inside. An ESP-NOW master holds no XBee panel, and it is the
                  transport whose schedules ARE dispatched as commands — so
                  without this the one site type whose nightly dispatch is
                  visible in field_commands was the one with nowhere to see it. */}
              {controller && controller.transport !== 'xbee' && commands.length > 0 && (
                <Card className="mb-4">
                  <CardContent className="pt-4">
                    <RecentActivity
                      results={[]}
                      commands={commands}
                      routers={routers}
                      transport={controller.transport}
                      onRefresh={load}
                      onShowLog={() => setTab('events')}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Getting a node INTO the list, as distinct from interrogating the
                  ones already in it. Separate panel because discovery is a
                  broadcast and can always miss: this is the address typed off the
                  radio's label, and the radios the bridge has overheard but is not
                  allowed to adopt on its own. XBee only, for the same reason the
                  diagnostics panel is. */}
              {controller?.transport === 'xbee' && (
                <div className="mb-4">
                  <NodeEnrolment
                    controllerId={controller.controller_id}
                    transport={controller.transport}
                    routers={routers}
                    online={controller.online}
                    onRefresh={load}
                  />
                </div>
              )}

              {/* Router cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {routers.map(router => (
                  <div
                    key={router.router_mac}
                    id={`node-${router.router_mac}`}
                    className={focusNodeMac === router.router_mac.toUpperCase()
                      ? 'rounded-xl ring-2 ring-blue-500 ring-offset-2'
                      : undefined}
                  >
                  <RouterDetailCard
                    controllerId={controller.controller_id}
                    transport={controller?.transport}
                    router={router}
                    schedule={schedByMac[router.router_mac] ?? null}
                    bridgeValves={bridgeValves}
                    timezoneOffsetSec={controller.timezone_offset ?? 0}
                    masterPumps={masterPumps}
                    onSaveLabels={(labels, relayCount, disabledRelays) => handleUpdateRelayLabels(router.router_mac, labels, relayCount, disabledRelays)}
                    onRename={name => handleUpdateRouterName(router.router_mac, name)}
                    isSuperAdmin={isSuperAdmin}
                    canRemove={isAdmin}
                    onRemove={() => removeRouter(
                      router.router_mac, router.name || router.router_mac.slice(-6))}
                    onUpdateFirmware={() => handleOtaUpdate('router', router.router_mac)}
                    otaBusy={otaSending === router.router_mac}
                    controllerOnline={!!controller.online}
                    stateUnknown={controller.coordinator_connected === false}
                    onToggleRelay={(relayIndex, currentlyOn) => handleToggleRelay(router.router_mac, relayIndex, currentlyOn)}
                    relayCommands={relayCommands}
                    onTestFinished={() => {
                      espnowService.getRouters(controllerId!).then(setRouters).catch(() => {});
                    }}
                  />
                  </div>
                ))}
                {routers.length === 0 && (
                  <p className="text-sm text-gray-400 col-span-full italic">No routers have announced themselves yet. Power on the ESP32 mesh.</p>
                )}
              </div>
            </div>
          )}

          {/* Settings tab */}
          {tab === 'settings' && (
            <div className="space-y-6">
              {/* Renders nothing unless this is an XBee laptop site. */}
              <BridgeUpdatePanel controller={controller} onRefreshController={load} />
              <SettingsTab
                controller={controller}
                farms={farms}
                savingFarm={savingFarm}
                onFarmChange={handleFarmChange}
                editingName={editingName}
                nameValue={nameValue}
                savingName={savingName}
                onStartRename={() => setEditingName(true)}
                onCancelRename={() => setEditingName(false)}
                onNameChange={setNameValue}
                onSaveName={saveName}
                isSuperAdmin={isSuperAdmin}
                otaSending={otaSending}
                onOtaMaster={() => handleOtaUpdate('master')}
                masterPumps={masterPumps}
                routers={routers}
                onAddPump={handleAddMasterPump}
                onUpdatePump={handleUpdateMasterPump}
                onDeletePump={handleDeleteMasterPump}
              />
            </div>
          )}

          {/* Schedules tab */}
          {tab === 'schedules' && (
            <div className="min-h-96">
              <RainSkipPanel
                controller={controller}
                programs={programs}
                routers={routers}
                orgTimezone={orgTimezone}
              />
              <EspNowScheduleBuilder
                controller={controller}
                initialProgramId={focusProgramId}
                programs={programs}
                routers={routers}
                masterPumps={masterPumps}
                orgTimezone={orgTimezone}
                onProgramsChange={setPrograms}
              />
            </div>
          )}

          {/* Map tab */}
          {tab === 'map' && (
            <EspNowLiveMap
              controller={controller}
              routers={routers}
              orgTimezone={orgTimezone}
              onPlaceRouter={handleRouterMapPlace}
              height={520}
            />
          )}

          {/* Sensors tab */}
          {tab === 'sensors' && controllerId && (
            <SensorsTab
              controllerId={controllerId}
              orgId={controller.organisation_id}
              routers={routers}
            />
          )}

          {/* Events tab */}
          {tab === 'events' && (
            <div className="space-y-8">
              {/* Commands first. When something did not happen, "what did we ask
                  and what did it say" is almost always the question, and the
                  answer used to be in the database and nowhere else. */}
              <CommandsTable commands={commands} routers={routers} />
              <EventsTable events={events} routers={routers} transport={controller?.transport} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
